import { Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Params, RouterStateSnapshot, TitleStrategy } from '@angular/router';

import { environment } from '../../../environments/environment';
import { environmentLabel } from '../../admin-v2/shell/environment-label';

/**
 * Browser tab titles, one pattern for the whole app (Anton, 2026-10-11):
 *
 *     [Staging] Locations · AACO Staging Test — Jet Fuel Tenders
 *
 * Most specific first, " · " between levels, " — <brand>" at the end. The page name is the
 * route's own `title` (Title Case, as the page heading or tab names it); the dynamic part
 * (a tender's or a company's name) is set by the page once its data has loaded, through
 * `PageTitleService.setName`. Outside production the environment goes in front, so tabs from
 * staging, sandbox and a local build can be told apart. A route without a title (the classic
 * pages) gets the brand alone.
 *
 * Nothing here makes a request: the name is whatever the page already shows.
 */

export const PAGE_TITLE_BRAND = 'Jet Fuel Tenders';
export const PAGE_TITLE_ADMIN_BRAND = 'Jet Fuel Tenders Admin';

/** The route data key for the title's extras (`PageTitleRouteData`). */
export const PAGE_TITLE_DATA = 'pageTitle';

/** Which kind of name a route shows; keeps a company id and a tender uid apart. */
export type PageTitleKind = 'tender' | 'company';

export interface PageTitleNameRule {
    kind: PageTitleKind;
    /** The route parameter whose value the name was set for (`:id`, `:uid`). */
    param: string;
    /** The name goes before the page ("Delta Air Lines · Airlines"), not after it. */
    first?: boolean;
    /** Shown until the name is known, or when there is none ("New Tender"). */
    fallback?: string;
}

/** `data: { pageTitle: … }` on a route. The deepest route that sets a field wins. */
export interface PageTitleRouteData {
    /** The brand at the end; the default is "Jet Fuel Tenders". */
    brand?: string;
    /** Which name follows the page; `null` turns an inherited rule off. */
    name?: PageTitleNameRule | null;
    /**
     * The page name from the URL, for a route whose resolvers do not run again when the part
     * that names the page changes (the wizard's `:step`, an error page's `:kind`). Wins over
     * the route's `title`; null falls back to it.
     */
    pageOf?: (route: ActivatedRouteSnapshot) => string | null;
}

/** What the route table says about the current page. */
export interface PageTitleRoute {
    page: string | null;
    brand: string;
    name: { kind: PageTitleKind; key: string; first: boolean; fallback: string | null } | null;
}

export const NO_PAGE_TITLE_ROUTE: PageTitleRoute = { page: null, brand: PAGE_TITLE_BRAND, name: null };

const ENVIRONMENT_PREFIXES = { production: '', staging: '[Staging] ', sandbox: '[Sandbox] ', local: '[Dev] ' } as const;

/** "[Staging] ", "[Sandbox] ", "[Dev] ", or nothing in production. Same source as the environment bar. */
export function environmentTitlePrefix(apiUrl: string): string {
    return ENVIRONMENT_PREFIXES[environmentLabel(apiUrl).kind];
}

/** The environment prefix of this build; a test provides its own. */
export const PAGE_TITLE_PREFIX = new InjectionToken<string>('PAGE_TITLE_PREFIX', {
    providedIn: 'root',
    factory: () => environmentTitlePrefix(environment.apiUrl)
});

/** Joins the parts: "[Staging] Locations · AACO Staging Test — Jet Fuel Tenders". */
export function composePageTitle(parts: { prefix?: string; brand: string; page?: string | null; name?: string | null; nameFirst?: boolean }): string {
    const page = parts.page?.trim() || null;
    const name = parts.name?.trim() || null;
    let levels = [page, name];
    if (name && name === page) {
        levels = [page];
    } else if (parts.nameFirst) {
        levels = [name, page];
    }
    const text = levels.filter((level): level is string => !!level).join(' · ');
    return (parts.prefix ?? '') + (text ? `${text} — ${parts.brand}` : parts.brand);
}

/** Reads the route chain, root to leaf: the deepest title, brand and name rule win. */
export function pageTitleRouteOf(snapshot: RouterStateSnapshot): PageTitleRoute {
    let node: ActivatedRouteSnapshot | null = snapshot.root;
    let page: string | null = null;
    let brand = PAGE_TITLE_BRAND;
    let rule: PageTitleNameRule | null = null;
    const params: Params = {};

    while (node) {
        Object.assign(params, node.params);
        const data = node.data?.[PAGE_TITLE_DATA] as PageTitleRouteData | undefined;
        const title = typeof node.title === 'string' ? node.title : null;
        page = data?.pageOf?.(node) ?? title ?? page;
        if (data?.brand) {
            brand = data.brand;
        }
        if (data && data.name !== undefined) {
            rule = data.name;
        }
        node = node.firstChild;
    }

    const key = rule ? params[rule.param] : null;
    return {
        page,
        brand,
        name: rule && key ? { kind: rule.kind, key: String(key), first: !!rule.first, fallback: rule.fallback ?? null } : null
    };
}

/**
 * The document title, kept from two inputs: the route (from `PageTitleStrategy` after every
 * navigation) and the name a page sets once its data has loaded. A name belongs to one record
 * (`kind` + key): it shows only while the URL names that same record, so one tender's name
 * can never title another's page, and moving between a tender's tabs keeps it.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleService {
    private readonly title = inject(Title);
    private readonly prefix = inject(PAGE_TITLE_PREFIX);

    private readonly route = signal<PageTitleRoute>(NO_PAGE_TITLE_ROUTE);
    private readonly names = signal<Partial<Record<PageTitleKind, { key: string; name: string }>>>({});

    /** The title as it stands. */
    public readonly text = computed(() => {
        const route = this.route();
        const stored = route.name ? this.names()[route.name.kind] : undefined;
        const name = route.name ? (stored && stored.key === route.name.key ? stored.name : route.name.fallback) : null;
        return composePageTitle({ prefix: this.prefix, brand: route.brand, page: route.page, name, nameFirst: route.name?.first });
    });

    /** The route part, after a navigation. */
    public setRoute(route: PageTitleRoute): void {
        this.route.set(route);
        this.apply();
    }

    /**
     * The name of the record a page shows, once it has loaded: `setName('tender', uid, name)`.
     * Only what the page itself shows; a blank name or key is ignored.
     */
    public setName(kind: PageTitleKind, key: string | number | null | undefined, name: string | null | undefined): void {
        const k = key === null || key === undefined ? '' : String(key);
        const n = (name ?? '').trim();
        if (!k || !n) {
            return;
        }
        const current = this.names()[kind];
        if (current?.key === k && current.name === n) {
            return;
        }
        this.names.update(names => ({ ...names, [kind]: { key: k, name: n } }));
        this.apply();
    }

    private apply(): void {
        const text = this.text();
        if (this.title.getTitle() !== text) {
            this.title.setTitle(text);
        }
    }
}

/** The router's title strategy (provided once, in AppModule): hands each route to `PageTitleService`. */
@Injectable({ providedIn: 'root' })
export class PageTitleStrategy extends TitleStrategy {
    private readonly titles = inject(PageTitleService);

    override updateTitle(snapshot: RouterStateSnapshot): void {
        this.titles.setRoute(pageTitleRouteOf(snapshot));
    }
}
